import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue'

import AnchorLocationCard from './cards/AnchorLocationCard.vue'
import RegularLegCard from './cards/RegularLegCard.vue'
import LButton from '@/components/core/Button.vue'
import { useContext } from '@/composables/context'
import { useSelectedTrip } from '@/composables/trip'
import type { GroupAnchorDraft } from '@/stores/transportation'
import { useTransportationGroupStore } from '@/stores/transportation-group'
import type { Transportation } from '@/types/transportation'
import type { LocationAutocompleteResult } from '@/types/trip'
import { formattedDuration } from '@/utils/date-utils'
import { effectiveGroupLegCount } from '@/utils/transportation-group-sync'
import { formattedDistance } from '@/utils/utility-manager'

// ─── Props & emits ────────────────────────────────────────────────────────

interface Props {
  /** Watched to snapshot the initial selection when the sheet opens. */
  open: boolean
  /** Segment currently being edited (used for seed-lock and group lookup). */
  transportation?: Pick<Transportation, '_id' | 'type' | 'groupId'>
  /** WP id at which the current segment lives — seed leg, locked. */
  seedFromWaypointId?: string
  /** v-model: selected fromWaypointIds. Live two-way bound with parent form. */
  value: string[]
  arrival?: GroupAnchorDraft
  departure?: GroupAnchorDraft
  seedIsArrival?: boolean
  seedIsDeparture?: boolean
  /** Origin name from the editing form (used when seed is an arrival anchor
   * that isn't persisted yet — picker reflects the form's location instead
   * of offering a duplicate search input). */
  seedOriginName?: string
  /** Destination name from the editing form (symmetric to seedOriginName). */
  seedDestinationName?: string
  /** Show cancel/apply footer. Off in live-coupled desktop mode. */
  showFooterActions?: boolean
  arrivalLocationInvalid?: boolean
  departureLocationInvalid?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LegPickerForm',
  props: {
    open: { type: Boolean },
    transportation: null,
    seedFromWaypointId: null,
    value: null,
    arrival: { default: () => ({ enabled: false }) },
    departure: { default: () => ({ enabled: false }) },
    seedIsArrival: { type: Boolean, default: false },
    seedIsDeparture: { type: Boolean, default: false },
    seedOriginName: null,
    seedDestinationName: null,
    showFooterActions: { type: Boolean, default: true },
    arrivalLocationInvalid: { type: Boolean },
    departureLocationInvalid: { type: Boolean }
  },
  emits: ["input", "update:arrival", "update:departure", "close"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', selection: string[]): void
  (e: 'update:arrival', value: GroupAnchorDraft): void
  (e: 'update:departure', value: GroupAnchorDraft): void
  (e: 'close'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { open: boolean, transportation?: Pick<Transportation, '_id' | 'type' | 'groupId'>, seedFromWaypointId?: string, value: string[], arrival: GroupAnchorDraft, departure: GroupAnchorDraft, seedIsArrival: boolean, seedIsDeparture: boolean, seedOriginName?: string, seedDestinationName?: string, showFooterActions: boolean, arrivalLocationInvalid?: boolean, departureLocationInvalid?: boolean };





// ─── Services ─────────────────────────────────────────────────────────────

const { i18n } = useContext()
const trip = useSelectedTrip()
const groupStore = useTransportationGroupStore()

// ─── Trip helpers ─────────────────────────────────────────────────────────

// Extracted from the template; the inline `wps[wps.length - 1]?.name` chain
// would be hard to read.
const lastWaypointName = computed(() => {
  const wps = trip.value?.waypoints ?? []
  return wps[wps.length - 1]?.name ?? ''
})

function routeMetaLine(route?: { duration?: number; distance?: number }) {
  if (!route) {
    return ''
  }
  const parts: string[] = []
  if (route.duration) {
    parts.push(formattedDuration(route.duration))
  }
  if (route.distance) {
    parts.push(formattedDistance(route.distance / 1000))
  }
  return parts.join(' · ')
}

// ─── Group context (persisted anchors) ────────────────────────────────────

const currentGroup = computed(() =>
  props.transportation
    ? groupStore.getGroupForTransportation(props.transportation)
    : undefined,
)

const persistedArrivalAnchor = computed(() => {
  const group = currentGroup.value
  const firstWp = trip.value?.waypoints[0]
  if (!group?.hasArrivalTransportation || !firstWp) {
    return undefined
  }
  return firstWp.transportations.find(
    (t) => t.groupId === group._id && t.isArrivalTransportation,
  )
})

const persistedDepartureAnchor = computed(() => {
  const group = currentGroup.value
  const wps = trip.value?.waypoints ?? []
  const lastWp = wps[wps.length - 1]
  if (!group || !lastWp || !groupStore.hasDepartureTransportation(group)) {
    return undefined
  }
  return lastWp.transportations.find(
    (t) => t.groupId === group._id && !t.isArrivalTransportation,
  )
})

// ─── Regular legs ─────────────────────────────────────────────────────────

const selectedFromIds = computed<Set<string>>(() => new Set(props.value))

const regularLegs = computed(() =>
  groupStore.trippedLegs.filter((l) => !!l.toWaypointId),
)

function isSeedLeg(fromWaypointId: string) {
  if (props.seedIsArrival || props.seedIsDeparture) {
    return false
  }
  return props.seedFromWaypointId === fromWaypointId
}

function findDrivingTransportationFor(fromWaypointId: string) {
  const wp = trip.value?.waypoints.find((w) => w._id === fromWaypointId)
  // Arrival transportations are surfaced as a separate anchor card, not
  // as a regular leg out of any waypoint.
  return wp?.transportations.find(
    (t) => t.type === 'driving' && !t.isArrivalTransportation,
  )
}

const legCards = computed(() => {
  const wps = trip.value?.waypoints ?? []
  return regularLegs.value.map((leg) => {
    const seed = isSeedLeg(leg.fromWaypointId)
    const checked = selectedFromIds.value.has(leg.fromWaypointId)
    const isLastLoopLeg =
      leg.toWaypointId === wps[0]?._id &&
      leg.toWaypointId !== leg.fromWaypointId

    const transport = findDrivingTransportationFor(leg.fromWaypointId)
    const parts: string[] = []
    const baseMeta = routeMetaLine(transport?.route)
    if (baseMeta) {
      parts.push(baseMeta)
    }
    if (seed) {
      parts.push(i18n.t('transportation.legPicker.currentLeg').toString())
    } else if (isLastLoopLeg) {
      parts.push(i18n.t('transportation.legPicker.returnsToStart').toString())
    }

    return {
      fromWaypointId: leg.fromWaypointId,
      fromName: leg.fromName,
      toName: leg.toName,
      checked,
      disabled: seed,
      metaLine: parts.join(' · '),
    }
  })
})

// ─── Header / counters ────────────────────────────────────────────────────

const tripMetaLine = computed(() => {
  const wpCount = trip.value?.waypoints.length ?? 0
  const tripName = trip.value?.name ?? ''
  const stops = i18n
    .t('transportation.legPicker.stopsCount', { count: wpCount })
    .toString()
  const legs = i18n
    .t('transportation.legPicker.legsCount', {
      count: regularLegs.value.length + 2,
    })
    .toString()
  return `${tripName} · ${stops} · ${legs}`
})

const effectiveLegCount = computed(() =>
  effectiveGroupLegCount({
    regularLegCount: selectedFromIds.value.size,
    arrivalEnabled: props.arrival.enabled,
    departureEnabled: props.departure.enabled,
  }),
)

// ─── Snapshot for cancel ──────────────────────────────────────────────────

const initialSelection = ref<string[]>([])
const initialArrival = ref<GroupAnchorDraft>({ enabled: false })
const initialDeparture = ref<GroupAnchorDraft>({ enabled: false })

watch(
  () => props.open,
  (isOpen, wasOpen) => {
    if (isOpen && !wasOpen) {
      initialSelection.value = [...props.value]
      initialArrival.value = { ...props.arrival }
      initialDeparture.value = { ...props.departure }
    }
  },
  { immediate: true },
)

// ─── Selection actions ────────────────────────────────────────────────────

function emitSelection(next: Set<string>) {
  // Only regular-leg seeds belong in fromWaypointIds. Arrival/departure
  // anchors live in their own flags on the group.
  if (
    props.seedFromWaypointId &&
    !props.seedIsArrival &&
    !props.seedIsDeparture
  ) {
    next.add(props.seedFromWaypointId)
  }
  emit('input', Array.from(next))
}

function toggleLeg(id: string, checked: boolean) {
  if (isSeedLeg(id) && !checked) {
    return
  }
  const next = new Set(selectedFromIds.value)
  if (checked) {
    next.add(id)
  } else {
    next.delete(id)
  }
  emitSelection(next)
}

function selectAllRegular() {
  const next = new Set<string>()
  for (const leg of regularLegs.value) {
    next.add(leg.fromWaypointId)
  }
  emitSelection(next)
}

function clearAll() {
  emitSelection(new Set())
  if (!props.seedIsArrival) {
    emit('update:arrival', { enabled: false })
  }
  if (!props.seedIsDeparture) {
    emit('update:departure', { enabled: false })
  }
}

// ─── Anchor actions ───────────────────────────────────────────────────────

function onArrivalChange(enabled: boolean) {
  emit('update:arrival', {
    enabled,
    location: enabled ? props.arrival.location : undefined,
  })
}

function onDepartureChange(enabled: boolean) {
  emit('update:departure', {
    enabled,
    location: enabled ? props.departure.location : undefined,
  })
}

function onArrivalLocationUpdate(next?: LocationAutocompleteResult) {
  // Picking a location implies enabling the anchor leg.
  emit('update:arrival', {
    enabled: next ? true : props.arrival.enabled,
    location: next ?? undefined,
  })
}

function onDepartureLocationUpdate(next?: LocationAutocompleteResult) {
  emit('update:departure', {
    enabled: next ? true : props.departure.enabled,
    location: next ?? undefined,
  })
}

// ─── Sheet actions ────────────────────────────────────────────────────────

function closeSheet() {
  emit('close')
}

function cancelChanges() {
  emit('input', [...initialSelection.value])
  emit('update:arrival', { ...initialArrival.value })
  emit('update:departure', { ...initialDeparture.value })
  emit('close')
}

return { __sfc: true,props, emit, i18n, trip, groupStore, lastWaypointName, routeMetaLine, currentGroup, persistedArrivalAnchor, persistedDepartureAnchor, selectedFromIds, regularLegs, isSeedLeg, findDrivingTransportationFor, legCards, tripMetaLine, effectiveLegCount, initialSelection, initialArrival, initialDeparture, emitSelection, toggleLeg, selectAllRegular, clearAll, onArrivalChange, onDepartureChange, onArrivalLocationUpdate, onDepartureLocationUpdate, closeSheet, cancelChanges, AnchorLocationCard, RegularLegCard, LButton }
}

})